/**
 * Measure caret/selection position inside a textarea using a mirror element.
 * Returns coordinates relative to the textarea's bounding rect.
 */
export function getSelectionRect(textarea, selectionStart, selectionEnd) {
  if (!textarea) return null;

  const text = textarea.value || '';
  const start = selectionStart ?? textarea.selectionStart ?? 0;
  const end = selectionEnd ?? textarea.selectionEnd ?? start;

  const mirror = document.createElement('div');
  const style = window.getComputedStyle(textarea);

  const props = [
    'fontFamily', 'fontSize', 'fontWeight', 'fontStyle',
    'letterSpacing', 'lineHeight', 'textTransform', 'wordSpacing',
    'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
    'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth',
    'boxSizing', 'whiteSpace', 'wordWrap', 'overflowWrap',
  ];

  mirror.style.position = 'absolute';
  mirror.style.visibility = 'hidden';
  mirror.style.pointerEvents = 'none';
  mirror.style.top = '0';
  mirror.style.left = '-9999px';
  mirror.style.width = `${textarea.clientWidth}px`;
  mirror.style.whiteSpace = 'pre-wrap';
  mirror.style.wordWrap = 'break-word';
  mirror.style.overflowWrap = 'break-word';

  props.forEach((prop) => {
    mirror.style[prop] = style[prop];
  });

  const before = document.createTextNode(text.slice(0, start));
  const marker = document.createElement('span');
  marker.textContent = text.slice(start, end) || '\u200b';
  const after = document.createTextNode(text.slice(end));

  mirror.appendChild(before);
  mirror.appendChild(marker);
  mirror.appendChild(after);
  document.body.appendChild(mirror);

  const markerRect = marker.getBoundingClientRect();
  const mirrorRect = mirror.getBoundingClientRect();
  const textareaRect = textarea.getBoundingClientRect();

  const top = markerRect.top - mirrorRect.top - textarea.scrollTop;
  const left = markerRect.left - mirrorRect.left - textarea.scrollLeft;
  const width = markerRect.width;
  const height = markerRect.height || parseFloat(style.lineHeight) || 20;

  document.body.removeChild(mirror);

  return {
    top: textareaRect.top + top,
    left: textareaRect.left + left,
    width,
    height,
    relativeTop: top,
    relativeLeft: left,
    textareaRect,
  };
}

/**
 * Position a floating toolbar above (or below) a selection rect, clamped to container.
 */
export function positionToolbar(selectionRect, toolbarWidth, toolbarHeight, containerRect) {
  if (!selectionRect) return { top: 0, left: 0, placement: 'above' };

  const centerX = selectionRect.relativeLeft + selectionRect.width / 2;
  let left = centerX - toolbarWidth / 2;
  let top = selectionRect.relativeTop - toolbarHeight - 8;
  let placement = 'above';

  const padding = 8;
  const maxLeft = (containerRect?.width || 400) - toolbarWidth - padding;
  left = Math.max(padding, Math.min(left, maxLeft));

  if (top < padding) {
    top = selectionRect.relativeTop + selectionRect.height + 8;
    placement = 'below';
  }

  return { top, left, placement };
}
